import { useEffect, type ReactNode } from 'react';
import { useSurface } from '../hooks/useSurface';

/**
 * One component, two renders — bottom sheet with a 24px top radius on mobile,
 * a 360px centered card with radius 16 on desktop. The spec picks by frame
 * (`dialogSheet = frame !== 'webdesktop'`); here that is the surface.
 */
export function Dialog({
  open,
  onDismiss,
  children,
  labelledBy,
}: {
  open: boolean;
  onDismiss: () => void;
  children: ReactNode;
  labelledBy?: string;
}) {
  const { isDesktop } = useSurface();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onDismiss]);

  if (!open) return null;

  return (
    <div
      className={isDesktop ? 'dialog-backdrop dialog-backdrop--centered' : 'dialog-backdrop'}
      onClick={onDismiss}
    >
      <div
        className="dialog-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

/** Alias kept because the plan names both shapes. */
export const BottomSheet = Dialog;
